import { HTMLAttributes, forwardRef } from "react";

import { cn } from "@oneleet/utils";

import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

const DEFAULT_COLOR = "accent";
const DEFAULT_POSITION = "top-right";
const DEFAULT_PING = false;
const INDICATOR_SIZE = "size-1.5";

const indicatorBase = cva(cn("absolute rounded-full", INDICATOR_SIZE), {
  variants: {
    position: {
      standalone: "origin-center",
      "top-right":
        "right-0 top-0 origin-bottom-left -translate-y-1/2 translate-x-1/2 transform",
      "top-left":
        "left-0 top-0 origin-bottom-right -translate-x-1/2 -translate-y-1/2 transform",
      "bottom-right":
        "bottom-0 right-0 origin-top-left translate-x-1/2 translate-y-1/2 transform",
      "bottom-left":
        "bottom-0 left-0 origin-top-right -translate-x-1/2 translate-y-1/2 transform",
      right:
        "right-0 top-1/2 origin-left -translate-x-1/2 translate-y-1/2 transform",
      left: "left-0 top-1/2 origin-right -translate-x-1/2 translate-y-1/2 transform",
    },
    color: {
      brand: "bg-brand-500",
      accent: "bg-ng-blue-800",
      gray: "bg-neutral-500",
      red: "bg-ng-red-800",
      green: "bg-ng-green-800",
      blue: "bg-ng-blue-800",
      yellow: "bg-ng-yellow-800",
    },
    ping: {
      true: "animate-ping",
      false: "",
    },
  },
  defaultVariants: {
    position: DEFAULT_POSITION,
    color: DEFAULT_COLOR,
    ping: DEFAULT_PING,
  },
});

type IndicatorProps = {
  classNames?: {
    container?: string;
    indicator?: string;
  };
} & VariantProps<typeof indicatorBase> &
  HTMLAttributes<HTMLDivElement>;

const Indicator = forwardRef<HTMLDivElement, IndicatorProps>(
  (
    { color, position, ping, className, classNames, children, ...props },
    ref,
  ) => {
    position = children === undefined ? "standalone" : position;

    return (
      <div
        {...props}
        ref={ref}
        className={cn(
          "relative",
          position === "standalone" && INDICATOR_SIZE,
          classNames?.container,
          className,
        )}
      >
        <div
          className={cn(
            indicatorBase({ color, position }),
            classNames?.indicator,
          )}
        />
        {ping && (
          <div
            className={cn(
              indicatorBase({ color, position, ping }),
              classNames?.indicator,
            )}
          />
        )}
        {children}
      </div>
    );
  },
);

Indicator.displayName = "Indicator";

export default Indicator;
